import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { getXAuthModule, Spinner } from '@core/components';
import { useBeforeMount } from '@core/hooks';
import { XApp } from '@core/utils';
import { FetchStatus, User } from './types';
import { tryLoginWithLsJwt } from './redux/actions';

// components
import NotAuthenticatedContent from './NotAuthenticatedContent/NotAuthenticatedContent';

// utils
import { axiosSetup } from './utils/axiosSetup';

interface XAuthProps {
  renderContent: () => JSX.Element;
}

const XAuth = ({ renderContent }: XAuthProps) => {
  const axiosInterceptor = useRef<number | null>(null);

  const dispatch = useDispatch();

  const { fetchStatus, jwt, refreshToken } = useSelector(
    ({ user }: { user: User }) => ({
      fetchStatus: user.fetchStatus,
      jwt: user.jwt,
      refreshToken: user.refreshToken
    })
  );

  useEffect(() => {
    dispatch(tryLoginWithLsJwt());
  }, []);

  useEffect(() => {
    axiosInterceptor.current = axiosSetup(
      jwt,
      refreshToken,
      dispatch,
      axiosInterceptor
    );
  }, [jwt, refreshToken]);

  useBeforeMount(() => {
    axios.defaults.baseURL = XApp.getApiUrlFromGlobal(['authApiUrl']);
  });

  if (
    fetchStatus === FetchStatus.USER_AUTH_CHECK ||
    fetchStatus === FetchStatus.UNSETTLED
  ) {
    return <Spinner />;
  }

  if (fetchStatus === FetchStatus.SUCCESS && jwt.length > 0) {
    return renderContent();
  }

  return <NotAuthenticatedContent />;
};

export const withXAuth = (WrappedComponent: React.FC<any>) => (props: any) => (
  <XAuth renderContent={() => <WrappedComponent {...props} />} />
);

export const MXAuth = {
  hoc: withXAuth,
  reduxModules: [getXAuthModule()]
};

export default XAuth;
